import React, { useEffect, useMemo, useState } from 'react';
import { Container, Card, CardBody } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import {
  FaTachometerAlt,
  FaBoxes,
  FaShoppingCart,
  FaCashRegister,
  FaShoppingBasket,
  FaTags,
  FaCog,
  FaUserCircle,
  FaSignOutAlt,
  FaMoon,
  FaSun,
  FaClock,
  FaSearch,
  FaChevronRight,
  FaLayerGroup
} from 'react-icons/fa';
import { authActions } from '../../sagas/authSlice';
import { ROLE_SUPER_ADMIN } from '../../helpers/constants';
import { POS_PAGE } from '../../routes/routeConstants';
import './ModuleSelector.css';

const ModuleSelector = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { fullName, businessName, role } = useSelector(state => state.auth);

  const [darkMode, setDarkMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');

  const modules = useMemo(() => [
    ...(role === ROLE_SUPER_ADMIN
      ? [{
        title: t('modules.dashboard.title'),
        icon: FaTachometerAlt,
        route: '/dashboard',
        group: 'Pilotage',
        description: 'Vue globale des indicateurs et activites'
      }]
      : []),
    {
      title: t('modules.sales.title'),
      icon: FaShoppingCart,
      route: '/sales-module',
      group: 'Gestion',
      description: 'Clients, documents commerciaux et suivi des ventes'
    },
    {
      title: t('modules.purchases.title'),
      icon: FaShoppingBasket,
      route: '/purchases-module',
      group: 'Gestion',
      description: 'Fournisseurs, achats et approvisionnements'
    },
    {
      title: t('modules.inventory.title'),
      icon: FaBoxes,
      route: '/inventory-module',
      group: 'Gestion',
      description: 'Stock, mouvements et disponibilite produits'
    },
    {
      title: t('modules.pos.title'),
      icon: FaShoppingCart,
      route: '/pos-module',
      group: 'Caisse',
      description: 'Operations de vente en point de vente'
    },
    {
      title: t('modules.posCashier.title', 'POS Caisse'),
      icon: FaCashRegister,
      route: POS_PAGE,
      group: 'Caisse',
      primary: true,
      description: 'Acces rapide a la caisse active'
    },
    {
      title: t('modules.shifts.title'),
      icon: FaClock,
      route: '/shifts-module',
      group: 'Caisse',
      description: 'Sessions, ouvertures et clotures de caisse'
    },
    {
      title: t('modules.promotion.title', 'Promotions'),
      icon: FaTags,
      route: '/promotion-module',
      group: 'Caisse',
      description: 'Offres, remises et campagnes commerciales'
    },
    {
      title: t('modules.settings.title'),
      icon: FaCog,
      route: '/settings-module',
      group: 'Administration',
      description: 'Parametres, utilisateurs et configuration'
    },
  ], [role, t]);

  const moduleGroups = useMemo(() => (
    [...new Set(modules.map(module => module.group))]
  ), [modules]);

  const visibleModules = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return modules
      .filter(module => selectedGroup === 'all' || module.group === selectedGroup)
      .filter(module => {
        if (!normalizedSearch) {
          return true;
        }

        return (
          module.title.toLowerCase().includes(normalizedSearch) ||
          module.group.toLowerCase().includes(normalizedSearch) ||
          module.description.toLowerCase().includes(normalizedSearch)
        );
      })
      .map(module => ({
        ...module,
        shortcut: modules.findIndex(item => item.route === module.route) + 1
      }));
  }, [modules, searchTerm, selectedGroup]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target?.closest?.('input, textarea, select, [contenteditable="true"]')) {
        return;
      }

      const idx = Number(e.key) - 1;
      if (idx >= 0 && idx < modules.length) {
        navigate(modules[idx].route);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modules, navigate]);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);

    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [darkMode]);

  const handleLogout = () => {
    Swal.fire({
      title: 'Confirmer la deconnexion',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Se deconnecter',
      cancelButtonText: 'Annuler'
    }).then((r) => {
      if (r.isConfirmed) {
        dispatch(authActions.logout());
        localStorage.clear();
        navigate('/login');
      }
    });
  };

  return (
    <div className="module-selector-wrapper">
      <header className="module-topbar">
        <Container className="module-topbar-content">
          <div className="brand-lockup" aria-label="Matjar POS">
            <span className="brand-mark">M</span>
            <div>
              <span className="brand-name">Matjar POS</span>
              <span className="brand-subtitle">Enterprise workspace</span>
            </div>
          </div>

          <div className="topbar-actions">
            <button
              className="theme-toggle"
              onClick={() => setDarkMode(v => !v)}
              title={darkMode ? 'Mode clair' : 'Mode sombre'}
              aria-label={darkMode ? 'Mode clair' : 'Mode sombre'}
            >
              {darkMode ? <FaSun /> : <FaMoon />}
            </button>

            <div className="user-pill">
              <FaUserCircle />
              <span>{fullName}</span>
            </div>

            <button className="logout-btn" onClick={handleLogout}>
              <FaSignOutAlt />
              <span>Deconnexion</span>
            </button>
          </div>
        </Container>
      </header>

      <main className="module-main">
        <Container className="module-workspace module-workspace-board">
          <section className="module-board">
            <div className="module-board-toolbar">
              <div className="module-filter-tabs" aria-label="Filtrer les modules">
                <button
                  type="button"
                  className={selectedGroup === 'all' ? 'active' : ''}
                  onClick={() => setSelectedGroup('all')}
                >
                  <FaLayerGroup />
                  Tous
                </button>
                {moduleGroups.map(group => (
                  <button
                    type="button"
                    key={group}
                    className={selectedGroup === group ? 'active' : ''}
                    onClick={() => setSelectedGroup(group)}
                  >
                    {group}
                  </button>
                ))}
              </div>

              <label className="module-search">
                <FaSearch />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Rechercher"
                />
              </label>
            </div>

            <div className="modules-grid-five">
              {visibleModules.map((m) => (
                <Card
                  key={m.route}
                  role="button"
                  tabIndex={0}
                  className={`module-card-dense ${m.primary ? 'primary' : ''}`}
                  onClick={() => navigate(m.route)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(m.route);
                    }
                  }}
                >
                  <CardBody className="module-card-body-dense">
                    <span className="shortcut">{m.shortcut}</span>
                    <span className="module-icon-shell">
                      <m.icon size={26} />
                    </span>
                    <div className="module-card-copy">
                      <span className="module-group-badge">{m.group}</span>
                      <div className="module-title-dense">{m.title}</div>
                      <p>{m.description}</p>
                    </div>
                    <span className="module-card-arrow"><FaChevronRight /></span>
                  </CardBody>
                </Card>
              ))}

              {visibleModules.length === 0 && (
                <div className="empty-modules">
                  Aucun module ne correspond a votre recherche.
                </div>
              )}
            </div>
          </section>
        </Container>
      </main>

      <footer className="app-footer">
        <Container className="footer-content">
          <span><strong>{businessName}</strong> - ERP & POS</span>
          <span>Copyright {new Date().getFullYear()}</span>
          <span>Built by <strong>IDERACLOUD</strong></span>
        </Container>
      </footer>
    </div>
  );
};

export default ModuleSelector;
